import {
  TldrawUiButton,
  TldrawUiButtonLabel,
  TldrawUiDialogBody,
  TldrawUiDialogFooter,
  TldrawUiDialogHeader,
  TLShapeId,
  useEditor,
} from "tldraw"
import { Text } from "@radix-ui/themes"
import {
  ParameterDefinitionChipBoolean,
  ParameterDefinitionChipColor,
  ParameterDefinitionChipNumber,
  ParameterDefinitionChipString,
} from "./ParameterDefinitionChip"
import { useState } from "react"
import { useCurrentPage } from "../hooks/useCurrentPage"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import { AddParameterChipButton2 } from "./AddParameterChipButton2"
import { StickerShape } from "../../shared/StickerShape"
import { Parameter } from "../../shared/Parameter"
import { Expr } from "../../shared/Expr"
import { LDPage } from "../../shared/LDPage"
import Delete from "@mui/icons-material/Delete"
import { SeamlessInput } from "./SeamlessInput"
import { noop } from "lodash"
import { EditParameter } from "./EditParameter"
import { PagePreview } from "./PagePreview"
import { css, DialogContent, styled } from "@mui/material"
import { ParamDefaults } from "./ParameterializableControl"
import { createParameter } from "../createParameter"

type Props = {
  open: boolean
  onClose: () => void
}

export function PageSettingsDialog2({ open, onClose }: Props) {
  const editor = useEditor()
  const [paramToDelete, setParamToDelete] = useState<string | null>(null)
  const [isEditingPageName, setIsEditingPageName] = useState(false)
  const [deleteParamDialogOpen, setDeleteParamDialogOpen] = useState(false)
  const [editParam, setEditParam] = useState<null | string>(null)
  const [layerIdsUsingParam, setLayerIdsUsingParam] = useState<string[]>([])
  const page = useCurrentPage()

  const [isWarning, setIsWarning] = useState(false)
  const pageParameters = page.meta.parameters
  const pageParameterValues = page.meta.parameterValues

  const disableDeletion = editor.getPages().length === 1
  const dependentShapes = editor.getPages().flatMap((p) => {
    const shapeIds = editor.getPageShapeIds(p.id)
    const dependentShapeIds: TLShapeId[] = []
    shapeIds.forEach((shapeId) => {
      const shape = editor.getShape(shapeId)!
      if (
        shape.type === "sticker" &&
        (shape as StickerShape).props.pageId === page.id
      ) {
        dependentShapeIds.push(shapeId)
      }
    })
    return dependentShapeIds
  })

  const dependentPages = editor.getPages().filter((p) => {
    const shapeIds = editor.getPageShapeIds(p.id)
    let isDependent = false
    shapeIds.forEach((shapeId) => {
      const shape = editor.getShape(shapeId)!
      isDependent =
        shape.type === "sticker" &&
        (shape as StickerShape).props.pageId === page.id
    })
    return isDependent
  })

  function deletePage() {
    if (dependentPages.length > 0) {
      setIsWarning(true)
      return
    }
    editor.deletePage(page.id)
    editor.deleteShapes(dependentShapes)
    onClose()
  }

  function deleteAnyway() {
    editor.deleteShapes(dependentShapes)
    editor.deletePage(page.id)
    onClose()
  }

  // const container = useContainer()

  const handleAddParameterClick = (type: Parameter["type"]) => {
    createParameter(type, editor)
  }

  function deleteParamAnyway(paramId: string) {
    const pageParameters = { ...page.meta.parameterValues }

    for (const layerId in page.meta.layers) {
      const layer = page.meta.layers[layerId]
      if (
        layer.fillColorOverride?.type === "ref" &&
        layer.fillColorOverride.id === paramId
      ) {
        const updatedLayer = {
          ...layer,
          fillColorOverride: Expr.lit(
            Expr.evaluate(layer.fillColorOverride, pageParameters),
          ),
        }

        editor.updatePage({
          id: page.id,
          meta: {
            ...page.meta,
            layers: { ...page.meta.layers, [layerId]: updatedLayer },
          },
        })
      }

      if (
        layer.strokeColorOverride?.type === "ref" &&
        layer.strokeColorOverride.id === paramId
      ) {
        const updatedLayer = {
          ...layer,
          strokeColorOverride: Expr.lit(
            Expr.evaluate(layer.strokeColorOverride, pageParameters),
          ),
        }

        editor.updatePage({
          id: page.id,
          meta: {
            ...page.meta,
            layers: { ...page.meta.layers, [layerId]: updatedLayer },
          },
        })
      }

      if (
        layer.strokeWidthOverride?.type === "ref" &&
        layer.strokeWidthOverride.id === paramId
      ) {
        const updatedLayer = {
          ...layer,
          strokeWidthOverride: Expr.lit(
            Expr.evaluate(layer.strokeWidthOverride, pageParameters),
          ),
        }

        editor.updatePage({
          id: page.id,
          meta: {
            ...page.meta,
            layers: { ...page.meta.layers, [layerId]: updatedLayer },
          },
        })
      }
    }

    const parameters = Object.fromEntries(
      Object.entries(page.meta.parameters).filter(([key]) => key !== paramId),
    )

    const meta = editor.getPage(page.id)?.meta

    // editor.updatePage({
    //   ...page,
    //   meta: {
    //     ...page.meta,
    //     parameters,
    //   },
    // })
    setDeleteParamDialogOpen(false)
    setIsWarning(false)
    setLayerIdsUsingParam([])
    setParamToDelete(null)
    editor.updatePage({
      ...page,
      meta: {
        ...meta,
        parameters,
      },
    })
  }

  function handleParamEdit(paramId: string) {
    setIsWarning(true)
    setEditParam(paramId)
  }

  function handleParamDelete(paramId: string) {
    setParamToDelete(paramId)
    console.log("delete param", paramId)
    const layers = Object.values(page.meta.layers)

    const idsUsingParam = layers
      .map((layer) => {
        if (
          (layer.strokeColorOverride?.type === "ref" &&
            layer.strokeColorOverride.id === paramId) ||
          (layer.fillColorOverride?.type === "ref" &&
            layer.fillColorOverride.id === paramId) ||
          (layer.strokeWidthOverride?.type === "ref" &&
            layer.strokeWidthOverride.id === paramId)
        ) {
          return layer.id
        }
      })
      .filter((id) => id !== undefined)

    if (idsUsingParam.length > 0) {
      setLayerIdsUsingParam(idsUsingParam)
      setIsWarning(true)
      setDeleteParamDialogOpen(true)
    } else {
      const parameters = Object.fromEntries(
        Object.entries(page.meta.parameters).filter(([key]) => key !== paramId),
      )

      editor.updatePage({
        ...page,
        meta: {
          ...page.meta,
          parameters,
        },
      })
    }
  }

  function handleParamNameChange(paramId: string, newName: string) {
    const page2 = editor.getCurrentPage() as LDPage
    const parameters2 = page2!.meta.parameters!

    const currentParamNames = Object.values(parameters2).map(
      (param) => param.name,
    )

    if (currentParamNames.includes(newName)) {
      console.warn(`Parameter with name ${newName} already exists`)
      return
    }

    const newParams = {
      ...page2.meta.parameters,
      [paramId]: {
        ...page2.meta.parameters[paramId],
        name: newName,
      },
    }

    const newParamValues = {
      ...page2.meta.parameterValues,
      [paramId]: {
        ...page2.meta.parameterValues[paramId],
      },
    }

    editor.updatePage({
      ...page2,
      meta: {
        ...page2.meta,
        parameters: newParams,
        parameterValues: newParamValues,
      },
    })
  }

  function handleParamDefaultValueChange<T extends string | number | boolean>(
    paramId: string,
    newValue: T,
  ) {
    console.log("current params", page.meta.parameters)
    console.log("changing", paramId, "to", newValue)
    const parameters = {
      ...page.meta.parameters,
      [paramId]: {
        ...page.meta.parameters[paramId],
        defaultValue: newValue,
      },
    }
    const parameterValues = {
      ...page.meta.parameterValues,
      [paramId]: Expr.lit(newValue),
    }

    editor.updatePage({
      ...page,
      meta: {
        ...page.meta,
        parameters,
        parameterValues,
      },
    })
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose()
        setIsWarning(false)
        setDeleteParamDialogOpen(false)
        setLayerIdsUsingParam([])
        setParamToDelete(null)
      }}
      fullWidth
    >
      {/* <TldrawUiDialogHeader className={dialogHeaderClassName}>
        <TldrawUiDialogTitle>Page Settings</TldrawUiDialogTitle>
        <TldrawUiDialogCloseButton />
      </TldrawUiDialogHeader> */}

      {!isWarning ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 16,
              fontSize: 18,
            }}
          >
            <DialogTitle>
              {isEditingPageName && (
                <SeamlessInput
                  value={page.name}
                  onBlur={function (value: string): void {
                    editor.updatePage({ id: page.id, name: value })
                    setIsEditingPageName(false)
                  }}
                  onInputChange={noop}
                />
              )}
              {!isEditingPageName && (
                <div onClick={() => setIsEditingPageName(true)}>
                  {page.name}
                </div>
              )}
            </DialogTitle>
          </div>
          {/* <div>
              <Tooltip title="Set Origin">
                <IconButton
                  onClick={() => {
                    onClose()
                    editor.setCurrentTool("origin")
                  }}
                >
                  <Disc />
                </IconButton>
              </Tooltip>
            </div> */}
          <DialogContent>
            <PagePreview pageId={page.id} />

            <Text size="2">Parameters</Text>

            <div
              style={{
                display: "flex",
                gap: 2,
                flexDirection: "column",
                border: "1px solid #ddd",
                padding: 4,
                background: "#f3f3f3",
              }}
            >
              {Object.entries(pageParameters ?? {}).map(([id, p]) => {
                if (p.type === "color") {
                  return (
                    <div key={id}>
                      <ParameterDefinitionChipColor
                        id={id}
                        name={p.name}
                        defaultValue={Expr.evaluate(
                          pageParameterValues[id],
                          {},
                        )}
                        onDelete={handleParamDelete}
                        //onEdit={handleParamEdit}
                        onNameChange={handleParamNameChange}
                        onDefaultValueChange={handleParamDefaultValueChange}
                      />
                    </div>
                  )
                }
                if (p.type === "number") {
                  return (
                    <div key={id}>
                      <ParameterDefinitionChipNumber
                        id={id}
                        name={p.name}
                        defaultValue={Expr.evaluate(
                          pageParameterValues[id],
                          {},
                        )}
                        onDelete={handleParamDelete}
                        onEdit={handleParamEdit}
                        onNameChange={handleParamNameChange}
                        onDefaultValueChange={handleParamDefaultValueChange}
                      />
                    </div>
                  )
                }
                if (p.type === "boolean") {
                  return (
                    <div key={id}>
                      <ParameterDefinitionChipBoolean
                        id={id}
                        name={p.name}
                        defaultValue={Expr.evaluate(
                          pageParameterValues[id],
                          {},
                        )}
                        onDelete={handleParamDelete}
                        // onEdit={handleParamEdit}
                        onNameChange={handleParamNameChange}
                        onDefaultValueChange={(id, newBool) =>
                          handleParamDefaultValueChange(id, newBool)
                        }
                      />
                    </div>
                  )
                }
                if (p.type === "string") {
                  return (
                    <div key={id}>
                      <ParameterDefinitionChipString
                        id={id}
                        name={p.name}
                        defaultValue={Expr.evaluate(
                          pageParameterValues[id],
                          {},
                        )}
                        onDelete={handleParamDelete}
                        onEdit={handleParamEdit}
                        onNameChange={handleParamNameChange}
                        onDefaultValueChange={(id, newBool) =>
                          handleParamDefaultValueChange(id, newBool)
                        }
                      />
                    </div>
                  )
                }

                return null
              })}

              <div>
                <AddParameterChipButton2
                  onAddParameter={handleAddParameterClick}
                />
              </div>
            </div>

            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={disableDeletion}
              onClick={deletePage}
              style={{ textTransform: "none", margin: "16px 0" }}
              startIcon={<Delete fontSize="inherit" />}
            >
              Delete Page
            </Button>
          </DialogContent>
        </>
      ) : deleteParamDialogOpen ? (
        <>
          <TldrawUiDialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
          </TldrawUiDialogHeader>
          <StyledTldrawUiDialogBody>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: 16,
              }}
            >
              <Text size="2">{`${layerIdsUsingParam.length} layer${
                layerIdsUsingParam.length > 1 ? "s" : ""
              } depend${layerIdsUsingParam.length < 2 ? "s" : ""} on this parameter:`}</Text>

              {layerIdsUsingParam.map((l) => (
                <div key={l}>
                  <Text size="2">
                    <b>{l}</b>
                  </Text>
                </div>
              ))}
            </div>
          </StyledTldrawUiDialogBody>
          <TldrawUiButton
            type="normal"
            onClick={() => {
              setIsWarning(false)
              setDeleteParamDialogOpen(false)
              setLayerIdsUsingParam([])
              setParamToDelete(null)
            }}
          >
            <TldrawUiButtonLabel>Cancel</TldrawUiButtonLabel>
          </TldrawUiButton>
          <TldrawUiButton
            //disabled={disableDeletion}
            type="danger"
            onClick={() => deleteParamAnyway(paramToDelete!)}
          >
            <TldrawUiButtonLabel>Delete</TldrawUiButtonLabel>
          </TldrawUiButton>
        </>
      ) : editParam ? (
        <>
          <EditParameter
            paramId={editParam}
            onClose={() => {
              setEditParam(null)
              setIsWarning(false)
            }}
          />
        </>
      ) : (
        <>
          <DialogTitle>Are you sure?</DialogTitle>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: 16,
            }}
          >
            <Text size="2">{`${dependentPages.length} page${
              dependentPages.length > 1 ? "s" : ""
            } depend${
              dependentPages.length === 1 ? "s" : ""
            } on this page:`}</Text>

            {dependentPages.map((p) => (
              <div key={p.id}>
                <Text size="2">
                  <b>{p.name}</b>
                </Text>
              </div>
            ))}
          </div>

          <TldrawUiButton type="normal" onClick={() => setIsWarning(false)}>
            <TldrawUiButtonLabel>Cancel</TldrawUiButtonLabel>
          </TldrawUiButton>
          <TldrawUiButton
            disabled={disableDeletion}
            type="danger"
            onClick={deleteAnyway}
          >
            <TldrawUiButtonLabel>Delete</TldrawUiButtonLabel>
          </TldrawUiButton>
        </>
      )}
    </Dialog>
  )
}

export const StyledTldrawUiDialogBody = styled(TldrawUiDialogBody)({
  padding: "16px",
  maxWidth: 450,
  minWidth: 400,
})
